import { RefFn, useLazyRef } from "@zap/utils/lib/ReactHelpers";
import * as React from "react";
import { useContext, useEffect, useRef } from "react";

type OneOrMoreElements = HTMLElement | ElementSet;
type ElementSet = Set<OneOrMoreElements>;

export interface IDomScope {
    elements: HTMLElement[];
    registrar: IDomRegistrar;
}

export function useDomScope() {
    let outerRegistrar = useContext(DomRegistrar);

    let elements = useLazyRef(() => new Set<OneOrMoreElements>()).current;

    let registrar: IDomRegistrar = React.useMemo(() => ({
        register(element: OneOrMoreElements | undefined) {
            if (element)
                elements.add(element);
        },
        deregister(element: OneOrMoreElements | undefined) {
            if (element)
                elements.delete(element);
        },
        useChildRef() {
            return domScopeChildRef(this);
        }
    }), []);

    useEffect(() => {
        outerRegistrar.register(elements);
        return () => outerRegistrar.deregister(elements);
    }, []);

    return { get elements() { return allElements(elements); }, registrar };
}

function allElements(elements: OneOrMoreElements): HTMLElement[] {
    if (elements instanceof Set)
        return Array.from(elements).flatMap(allElements);
    return [elements];
}

export function useDomScopeChild() {
    return domScopeChildRef(useContext(DomRegistrar));
}

function domScopeChildRef(registrar: IDomRegistrar) {
    let childRef = useRef<HTMLElement>();
    useEffect(() => {
        let childElement = childRef.current;
        registrar.register(childElement);
        () => registrar.deregister(childElement);
    });
    return (el: HTMLElement) => childRef.current = el;
}

export interface IDomRegistrar {
    register(element: OneOrMoreElements | undefined): void;
    deregister(element: OneOrMoreElements | undefined): void;
    useChildRef(): RefFn<HTMLElement>;
}

export const DomRegistrar = React.createContext<IDomRegistrar>({ register() { }, deregister() { }, useChildRef: () => () => { } });